import * as React from "react";
import {observer} from "mobx-react";
import {withAppStores} from "../../../AppStores";
import {Button} from "../button/Button";
import {ReactUtils} from "../../utils/ReactUtils";
import type {AppState} from "../../../data/state/AppState";
import {TimeUtils} from "../../../utils/TimeUtils";
import type {IModel, IModelData} from "../../../data/models/Model";
import type {Field, IFieldAdapter} from "../../../data/models/field/Field";
import {FieldDataType, Permission, XyiconFeature} from "../../../generated/api/base";
import {KeyboardListener} from "../../../utils/interaction/key/KeyboardListener";
import {LookupUtils} from "../../modules/abstract/sidepanel/tabs/details/LookupUtils";
import {StringUtils} from "../../../utils/data/string/StringUtils";
import type {Space} from "../../../data/models/Space";
import {DomPortal} from "../../modules/abstract/portal/DomPortal";
import {InfoBubble} from "../../modules/abstract/common/infobutton/InfoBubble";
import type {TransformObj} from "../../../utils/dom/DomUtils";
import {DomUtils, HorizontalAlignment, VerticalAlignment} from "../../../utils/dom/DomUtils";
import type {Catalog} from "../../../data/models/Catalog";
import {getFieldTypesWithDropdown} from "../../../data/models/field/FieldUtils";
import {TableCellValue} from "./TableCellValue";

interface ITableCellProps {
	readonly style: React.CSSProperties;
	readonly field: IFieldAdapter;
	readonly fieldValue: any;
	readonly item: IModel;
	readonly columnIndex?: number;
	readonly appState?: AppState;
	readonly selectedItems?: IModel[];
	readonly scrollTableToShowFullCell?: (e: React.MouseEvent) => void;
}

interface ITableCellState {
	inEditMode: boolean;
	isChanged: boolean;
	value: string;
	caretPosition: number;
	expand: boolean;
	errorMessage: string;
	toolTipTransform: TransformObj;
}

@withAppStores("appState")
@observer
export class TableCell extends React.Component<ITableCellProps, ITableCellState> {
	private _tableCell = React.createRef<HTMLDivElement>();
	private _floating = React.createRef<HTMLDivElement>();

	constructor(props: ITableCellProps) {
		super(props);
		this.state = {
			inEditMode: false,
			isChanged: false,
			value: props.appState.actions.getOwnFieldValue(props.item, props.field?.refId),
			caretPosition: 0,
			expand: false,
			errorMessage: "",
			toolTipTransform: null,
		};
	}

	private get isUpdating(): boolean {
		const {item, field, appState} = this.props;

		if (!item || !field || !appState) {
			return false;
		}

		return appState.itemFieldUpdateManager.isItemFieldBeingUpdated(item.id, field.refId, item.ownFeature);
	}

	private onEditCell = (e: React.MouseEvent) => {
		const {selectedItems, item, scrollTableToShowFullCell, fieldValue} = this.props;
		const isCellEditable = this.isCellEditable;
		const sel = window?.getSelection();

		if (sel?.rangeCount) {
			const range = sel.getRangeAt(0);
			const preRange = range.cloneRange();
			const element = this._tableCell.current;
			const cellContent = this.splitCellContent(fieldValue);

			preRange.selectNodeContents(element);
			preRange.setEnd(range.endContainer, range.endOffset);

			let caretPosition = preRange.toString().length;

			if (Array.isArray(cellContent)) {
				let i = 0;
				let sum = 0;

				while (!isNaN(sum) && sum < caretPosition) {
					sum += (cellContent[i] as string)?.length;
					i++;
				}

				if (!isNaN(sum)) {
					// caretPosition will be the same in end of n. line and start of (n + 1). line
					// if endOffset is 0, we know that we are in the (n + 1). line, so caretPosition should be incremented
					if (caretPosition === sum && preRange.endOffset === 0) {
						caretPosition++;
					}
					caretPosition += i - 1;
				} else {
					caretPosition = 0;
				}
			}

			this.setState({caretPosition});
		}

		scrollTableToShowFullCell?.(e);

		if ((this.isSpaceViewRendererMounted && isCellEditable) || selectedItems.some((i) => i.id === item.id)) {
			e.stopPropagation();
		}

		if (isCellEditable && !this.state.inEditMode) {
			this.setState({inEditMode: true});
		}
	};

	private onKeyboardPress = (event: KeyboardEvent) => {
		if (this.isCellEditable && this.state.inEditMode) {
			switch (event.key) {
				case KeyboardListener.KEY_ESCAPE:
					this.setState({inEditMode: false});
					break;
			}
		}
	};

	private onBlur = () => {
		const {appState, field, selectedItems, item} = this.props;
		const {value, isChanged, errorMessage} = this.state;

		this.setState({inEditMode: false});

		if (isChanged && !errorMessage) {
			const itemsToCheckPermission = this.isSpaceViewRendererMounted ? [item] : selectedItems;
			const itemsToUpdate = this.getItemsWithPermission(itemsToCheckPermission);
			let refId = field.refId;

			// if the Xyicon Model in Catalog Grid or Portfolio / Space name is changed inline, the refId should not include the feature number
			if (this.isNameOrModel) {
				refId = refId.split("/")[1];

				// changing item's value to avoid cell value go back to previous and then update to new
				item[refId as keyof IModelData] = value;
			}

			this.setState({isChanged: false});
			TimeUtils.waitUpdate(appState.actions.updateFields(itemsToUpdate, {[refId]: value}), this.props.appState.app.notificationContainer);
		}
	};

	private onChange = (value: string) => {
		const {appState, field, selectedItems, item} = this.props;

		// onChange messes up the other field types, however SingleSelect, MultiSelect, Boolean and DateTime needs it
		if ([...getFieldTypesWithDropdown(), FieldDataType.Boolean, FieldDataType.Email, FieldDataType.Numeric].includes(field.dataType)) {
			const itemsToCheckPermission = selectedItems.includes(item) ? selectedItems : [item];
			const itemsToUpdate = this.getItemsWithPermission(itemsToCheckPermission);

			TimeUtils.waitUpdate(appState.actions.updateFields(itemsToUpdate, {[field.refId]: value}), this.props.appState.app.notificationContainer);
		}
	};

	private onInput = (value: string) => {
		const errorMessage = this.getErrorMessage(value);
		this.setState({value, isChanged: true, errorMessage});
	};

	private onDoubleClick = (e: React.MouseEvent) => {
		if (this.state.inEditMode) {
			e.stopPropagation();
		}
	};

	private splitCellContent = (value: string) => {
		if (typeof value === "string") {
			if (value.includes(";")) {
				return value.split(";");
			} else if (value.includes("\n")) {
				return value.split("\n");
			}
		}

		return value;
	};

	private getFieldValue = (item: IModel, field: IFieldAdapter) => {
		const {appState} = this.props;
		const fieldInherited =
			(field?.feature === XyiconFeature.Portfolio && item.ownFeature === XyiconFeature.Space) ||
			([XyiconFeature.XyiconCatalog, XyiconFeature.Portfolio, XyiconFeature.Space].includes(field?.feature) &&
				[XyiconFeature.Boundary, XyiconFeature.Xyicon].includes(item.ownFeature));

		// getOwnFieldValue doesn't return anything if the field is inherited from other feature. In that case, the renderValue should be used
		if (!fieldInherited && (field?.dataType === FieldDataType.Boolean || field?.dataType === FieldDataType.MultipleChoiceList)) {
			return appState.actions.getOwnFieldValue(item, field?.refId);
		}
		return appState.actions.renderValue(item, field?.refId);
	};

	private getInheritedValues = () => {
		const {appState, item, field} = this.props;
		const xxLinks = appState.actions.getLinksXyiconXyicon(item.id);
		const bbLinks = appState.actions.getLinksBoundaryBoundary(item.id);
		const xbLinks = appState.actions.getLinksXyiconBoundary(item.id);
		const linkItems = item.ownFeature === XyiconFeature.Xyicon ? [...xxLinks, ...xbLinks] : bbLinks;

		const fieldIdToGetInheritedValues = appState.actions.getFieldsForType(item.typeId, field?.feature).find((f) => f.id === (field as Field).id);
		const customField = fieldIdToGetInheritedValues && appState.actions.getFieldById(fieldIdToGetInheritedValues.id);
		const isLookupField = LookupUtils.isLookupField(field, item, appState.actions);

		if (
			customField &&
			customField.displayOnLinks &&
			![XyiconFeature.XyiconCatalog, XyiconFeature.Portfolio, XyiconFeature.Space].includes(customField.feature) &&
			!isLookupField
		) {
			return linkItems
				.map((l) => {
					return this.getFieldValue(l.object, customField);
				})
				.filter(
					(e) => e != undefined && e != null && ((customField.dataType === FieldDataType.Boolean && !this.isXyiconBoundaryInheritedField) || e != ""),
				);
		}
		return [];
	};

	private getItemsWithPermission = (itemsToCheck: IModel[]) => {
		const {field, appState} = this.props;

		if (this.isNameOrModel) {
			return itemsToCheck.filter((item) => this.isTypeOrNameEditable(item));
		}

		return itemsToCheck.filter((item) => appState.actions.getFieldPermission(field, [item]) > Permission.View);
	};

	private expandCell = (e: React.MouseEvent) => {
		e.stopPropagation();
		this.setState((prevState) => ({expand: !prevState.expand}));
	};

	private getButtonLabel(inheritedValues: string | string[]) {
		const {expand} = this.state;

		if (expand) {
			return "Less";
		}

		return `${inheritedValues.length - 10} More`;
	}

	private isTypeOrNameEditable(item: IModel) {
		const {appState} = this.props;
		const {user, actions} = appState;

		let permission = Permission.None;

		if (item.ownFeature === XyiconFeature.Portfolio) {
			permission = actions.getPortfolioPermission(item.id);
		} else if (item.ownFeature === XyiconFeature.XyiconCatalog) {
			permission = user?.isAdmin
				? Permission.Delete
				: (user?.organizationFeaturePermissionList.find((p) => p.feature === XyiconFeature.XyiconCatalog).permission ?? Permission.None);
		} else {
			permission = actions.getModuleTypePermission(item.typeId, item.ownFeature);
		}

		return permission > Permission.View;
	}

	private getErrorMessage = (value: string) => {
		const {field, appState, item} = this.props;

		// Name validation
		if (this.isNameOrModel) {
			if (!value.trim()) {
				return "Name cannot be empty";
			}

			if (
				(item.ownFeature === XyiconFeature.Portfolio && !appState.actions.isPortfolioNameValid(value, item.id)) ||
				(item.ownFeature === XyiconFeature.Space && !appState.actions.isSpaceNameValid(value, item as Space)) ||
				(item.ownFeature === XyiconFeature.XyiconCatalog && !appState.actions.isModelValidForCatalog(value, item as Catalog))
			) {
				return "Name needs to be unique";
			}
		}

		// Email validation
		if (field.dataType === FieldDataType.Email) {
			return !value || StringUtils.emailValidator(value) ? "" : "Enter a valid Email!";
		}

		// Number validation is not needed because it's not updated on input

		// DateTime validation is not implemented
	};

	private get isCellEditable() {
		const {field, item, appState, selectedItems} = this.props;
		const {actions, user} = appState;

		if (field && item && user) {
			// We don't support updating multiple fields that have validation
			// Simply because the the first validation to be implemented is the "UniqueGIAI" validation
			// Which means all values must be unique. So if we're about to update more than one of them with the same value
			// It would lead to problems, or at least invalid values.
			// If we say boolean fields are not editable, then the little toggler is not visible, meaning it breaks the animation of the toggleswitch
			// as soon you click it, because it becomes non editable while we're waiting for the response from the backend.
			// So for boolean fields, and multiplechoicelist, we're making an exception
			if (
				(field.hasValidation && this.props.selectedItems.length > 1) ||
				(this.isUpdating &&
					![
						FieldDataType.SingleChoiceList,
						FieldDataType.MultipleChoiceList,
						FieldDataType.Boolean,
						FieldDataType.PeopleSingleChoiceList,
						FieldDataType.PeopleMultipleChoiceList,
					].includes(field.dataType))
			) {
				return false;
			}
			// 'assigned' will be undefined for system/readonly fields (id, type, lastModifiedBy, lastModifiedAt, model, name)
			const isAssigned = actions.getFieldsForType(item.typeId, field.feature).find((f) => f.id === (field as Field).id)?.value;
			const hasPermission = actions.getFieldPermission(field, [item]) > Permission.View;
			const isInherited = field.displayOnLinks && field.feature !== item.ownFeature && !Object.keys(item.fieldData).includes(field.refId);
			const isHidden = appState.actions.isFieldHiddenByMasking(item, field);

			// "<feature>/type" fields are type fields and they are editable, except the Xyicon type, it's tied to the Model
			const isType =
				(field.refId.includes("type") && ![XyiconFeature.Xyicon].includes(item.ownFeature)) ||
				(field.refId.includes("model") && item.ownFeature === XyiconFeature.Xyicon);
			const isTypeOrNameEditable = this.isTypeOrNameEditable(item);

			return (
				this.editableFieldTypes.includes(field.dataType) &&
				!field.hasFormula &&
				!isInherited &&
				!isHidden &&
				((isAssigned && hasPermission) ||
					(isType && isTypeOrNameEditable) ||
					(this.isNameOrModel && !(selectedItems.length > 1) && isTypeOrNameEditable))
			);
		}
		return false;
	}

	private get editableFieldTypes() {
		return [
			FieldDataType.Type,
			FieldDataType.SingleLineText,
			FieldDataType.MultiLineText,
			FieldDataType.Numeric,
			FieldDataType.SingleChoiceList,
			FieldDataType.Boolean,
			FieldDataType.DateTime,
			FieldDataType.MultipleChoiceList,
			FieldDataType.PeopleSingleChoiceList,
			FieldDataType.PeopleMultipleChoiceList,
			FieldDataType.Email,
		];
	}

	private get isNameOrModel() {
		// Portfolio or Space name or Catalog model
		return [`${XyiconFeature.Portfolio}/name`, `${XyiconFeature.Space}/name`, `${XyiconFeature.XyiconCatalog}/model`].includes(
			this.props.field?.refId,
		);
	}

	private get isSpaceViewRendererMounted() {
		return this.props.appState.app.spaceViewRenderer.isMounted;
	}

	private get isXyiconBoundaryInheritedField() {
		const {field, item} = this.props;

		return item.ownFeature === XyiconFeature.Xyicon && field?.feature === XyiconFeature.Boundary;
	}

	public override componentDidMount() {
		KeyboardListener.getInstance().signals.down.add(this.onKeyboardPress);
	}

	public override componentWillUnmount(): void {
		KeyboardListener.getInstance().signals.down.remove(this.onKeyboardPress);
	}

	public override componentDidUpdate(prevProps: ITableCellProps, prevState: ITableCellState) {
		if (prevProps.style.width !== this.props.style.width || prevProps.fieldValue !== this.props.fieldValue) {
			this.forceUpdate();
		}

		if (prevState.inEditMode && !this.state.inEditMode && this.state.errorMessage) {
			this.setState({errorMessage: ""});
		}

		if (!prevState.errorMessage && this.state.errorMessage) {
			this.setState({
				toolTipTransform: DomUtils.getFixedFloatingElementPosition(
					this._tableCell.current,
					this._floating.current,
					VerticalAlignment.topOuter,
					HorizontalAlignment.center,
					0,
					0,
					true,
				),
			});
		}
	}

	public override render() {
		const {style, field, appState, item, fieldValue, selectedItems} = this.props;
		const {inEditMode, caretPosition, expand, errorMessage, toolTipTransform} = this.state;
		const isFieldHidden = appState.actions.isFieldHiddenByMasking(item, field);
		const isEditable = this.isCellEditable;
		const isUpdating = this.isUpdating;

		const inheritedValues = field ? this.getInheritedValues() : [];
		const ownValue =
			field?.refId === `${XyiconFeature.XyiconCatalog}/icon` || field?.refId === `${XyiconFeature.Xyicon}/icon`
				? fieldValue
				: this.getFieldValue(item, field);

		const isInputComponentBooleanField =
			field?.dataType === FieldDataType.Boolean &&
			!isFieldHidden &&
			appState.actions.getFieldRefIdsForType(item.typeId, item.ownFeature).includes(field.refId);
		const cellContent = this.splitCellContent(ownValue || fieldValue);

		const isArray = Array.isArray(cellContent);

		const inlineStyle: React.CSSProperties = this._floating && {
			top: "-50px",
			left: "-80px",
			position: "absolute",
			zIndex: "99999999",
			transform: toolTipTransform?.translate,
		};

		return (
			<div
				className={ReactUtils.cls("td TableCell", {
					italic: !fieldValue && item.ownFeature === XyiconFeature.User,
					editable: isEditable,
					array: isArray,
					date: field?.dataType === FieldDataType.DateTime,
					boolean: isInputComponentBooleanField,
					emptySingleSelectOrDateTime: [FieldDataType.SingleChoiceList, FieldDataType.DateTime].includes(field?.dataType) && !fieldValue,
					textAlignRight: [FieldDataType.Numeric].includes(field?.dataType),
					multiline: field?.dataType === FieldDataType.MultiLineText,
				})}
				data-table-column-index={this.props.columnIndex}
				ref={this._tableCell}
				style={style}
				onBlur={this.onBlur}
				onDoubleClick={this.onDoubleClick}
			>
				{!this.isXyiconBoundaryInheritedField && ( // if a xyicon has a boundary inherited field, the own field value will be null, so we need the inherited value. In that case we must not render the own value's empty space in the grid: ;
					<TableCellValue
						value={field ? ownValue : fieldValue}
						field={field}
						item={item}
						isEditable={isEditable}
						isUpdating={isUpdating}
						disabled={false}
						selectedItems={selectedItems}
						onBlur={this.onBlur}
						onClick={this.onEditCell}
						onInput={this.onInput}
						onChange={this.onChange}
						inEditMode={inEditMode}
						caretPosition={caretPosition}
						className={ReactUtils.cls({
							textCursor:
								[FieldDataType.MultiLineText, FieldDataType.Numeric, FieldDataType.SingleLineText, FieldDataType.Email].includes(field?.dataType) &&
								!LookupUtils.isLookupField(field, item, appState.actions),
						})}
					/>
				)}
				<div className="vbox">
					{field &&
						inheritedValues.length > 0 &&
						inheritedValues.map((inheritedVal, index) => {
							if (index < 10 || expand) {
								return (
									<TableCellValue
										key={index}
										value={inheritedVal}
										field={field}
										item={item}
										isEditable={false}
										isUpdating={false}
										disabled={true}
										selectedItems={selectedItems}
									/>
								);
							}

							return null;
						})}
				</div>
				{inheritedValues.length > 10 && (
					<Button
						label={this.getButtonLabel(inheritedValues)}
						className="naked"
						onClick={this.expandCell}
					/>
				)}
				{errorMessage && (
					<div
						ref={this._floating}
						className="infoIcon editing"
					>
						<DomPortal destination={appState.app.modalContainer}>
							<InfoBubble
								content={errorMessage}
								isErrorMessage={true}
								divRef={this._floating}
								style={inlineStyle}
							/>
						</DomPortal>
					</div>
				)}
			</div>
		);
	}
}
